import { useCallback, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";

import { PatientFormData } from "components/views/patientDetails/patientEdit";
import { getExhClient, isExhClientInitialized } from "lib/extrahorizon/client";
import { extractValidationErrors } from "lib/extrahorizon/utils";
import { ExhPatientData } from "types/patient";

import { convertToExhPatientData } from "../utils/patientConverters";

export type CreatePatientResult =
  | { status: "success"; data: ExhPatientData }
  | { status: "validation_error"; errors: Record<string, string> };

interface UseCreateExhPatientResult {
  createPatient: (formData: PatientFormData) => Promise<CreatePatientResult | null>;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook for creating patients via ExtraHorizon client
 * Uses the patient schema which is intercepted and redirected to patient-endpoint
 */
export const useCreateExhPatient = (): UseCreateExhPatientResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { showBoundary: onError } = useErrorBoundary();

  const createPatient = useCallback(
    async (formData: PatientFormData): Promise<CreatePatientResult | null> => {
      if (!isExhClientInitialized()) {
        const err = new Error("ExtraHorizon client not initialized");
        setError(err);
        return null;
      }

      try {
        setLoading(true);
        setError(null);

        const client = getExhClient();
        const exhData = convertToExhPatientData(formData);

        const result = await client.data.documents.create("patient", exhData);

        return { status: "success", data: result as ExhPatientData };
      } catch (err) {
        const validationErrors = extractValidationErrors(err);
        if (validationErrors) {
          return { status: "validation_error", errors: validationErrors };
        }
        const error = err instanceof Error ? err : new Error("Failed to create patient");
        setError(error);
        onError(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [onError]
  );

  return { createPatient, loading, error };
};
